/**
 * Connection Point Utilities
 * 
 * Functions for calculating optimal connection points on nodes.
 * Determines which side of a node (top, bottom, left, right) an edge should connect to.
 */

import { NODE_WIDTH, NODE_HEIGHT, CLEARANCE } from '../constants';

/**
 * Connection sides enum
 */
export const ConnectionSide = {
  TOP: 'top',
  BOTTOM: 'bottom',
  LEFT: 'left',
  RIGHT: 'right',
};

/**
 * Get the connection point coordinates for a given side of a node
 * @param {Object} pos - Node position { x, y }
 * @param {Object} dims - Node dimensions { width, height }
 * @param {string} side - Which side to connect from (top, bottom, left, right)
 * @param {number} offset - Optional offset along the side (for multiple connections)
 * @returns {Object} { x, y } coordinates of the connection point
 */
export function getConnectionPoint(pos, dims, side, offset = 0) {
  // Guard against null/undefined position
  if (!pos || pos.x == null || pos.y == null) {
    console.warn('[getConnectionPoint] Invalid position:', pos);
    return { x: 0, y: 0 };
  }
  
  const width = dims?.width || NODE_WIDTH;
  const height = dims?.height || NODE_HEIGHT;
  
  switch (side) {
    case ConnectionSide.TOP:
      return { x: pos.x + width / 2 + offset, y: pos.y };
    case ConnectionSide.BOTTOM:
      return { x: pos.x + width / 2 + offset, y: pos.y + height };
    case ConnectionSide.LEFT:
      return { x: pos.x, y: pos.y + height / 2 + offset };
    case ConnectionSide.RIGHT:
      return { x: pos.x + width, y: pos.y + height / 2 + offset };
    default:
      return { x: pos.x + width / 2 + offset, y: pos.y + height }; // Default to bottom
  }
}

/**
 * Calculate the base connection point for a given side of a node
 * @param {Object} nodePos - Node position { x, y }
 * @param {Object} nodeDims - Node dimensions { width, height }
 * @param {string} side - Which side (top, bottom, left, right)
 * @returns {Object} { baseX, baseY } coordinates of the center of the side
 */
export function getBaseConnectionPoint(nodePos, nodeDims, side) {
  const nodeWidth = nodeDims?.width || NODE_WIDTH;
  const nodeHeight = nodeDims?.height || NODE_HEIGHT;
  
  if (side === 'top') {
    return { baseX: nodePos.x + nodeWidth / 2, baseY: nodePos.y };
  } else if (side === 'bottom') {
    return { baseX: nodePos.x + nodeWidth / 2, baseY: nodePos.y + nodeHeight };
  } else if (side === 'left') {
    return { baseX: nodePos.x, baseY: nodePos.y + nodeHeight / 2 };
  } else { // right
    return { baseX: nodePos.x + nodeWidth, baseY: nodePos.y + nodeHeight / 2 };
  }
}

/**
 * Calculate the optimal connection sides for two nodes based on their relative positions.
 * Chooses sides that minimize the path length while maintaining clean orthogonal routing.
 * 
 * @param {Object} fromPos - Source node position { x, y }
 * @param {Object} toPos - Target node position { x, y }
 * @param {Object} fromDims - Source node dimensions { width, height }
 * @param {Object} toDims - Target node dimensions { width, height }
 * @returns {Object} { fromSide, toSide, pathType }
 */
export function calculateOptimalConnection(fromPos, toPos, fromDims, toDims) {
  // Guard against null/undefined positions
  if (!fromPos || fromPos.x == null || fromPos.y == null ||
      !toPos || toPos.x == null || toPos.y == null) {
    console.warn('[calculateOptimalConnection] Invalid positions:', { fromPos, toPos });
    return { fromSide: ConnectionSide.BOTTOM, toSide: ConnectionSide.TOP, pathType: 'downward' };
  }
  
  const fromWidth = fromDims?.width || NODE_WIDTH;
  const fromHeight = fromDims?.height || NODE_HEIGHT;
  const toWidth = toDims?.width || NODE_WIDTH;
  const toHeight = toDims?.height || NODE_HEIGHT;
  
  // Calculate node centers
  const fromCenterX = fromPos.x + fromWidth / 2;
  const fromCenterY = fromPos.y + fromHeight / 2;
  const toCenterX = toPos.x + toWidth / 2;
  const toCenterY = toPos.y + toHeight / 2;
  
  // Calculate node edges
  const fromTop = fromPos.y;
  const fromBottom = fromPos.y + fromHeight;
  const fromLeft = fromPos.x;
  const fromRight = fromPos.x + fromWidth;
  
  const toTop = toPos.y;
  const toBottom = toPos.y + toHeight;
  const toLeft = toPos.x;
  const toRight = toPos.x + toWidth;
  
  // Calculate relative position
  const dx = toCenterX - fromCenterX;
  const dy = toCenterY - fromCenterY;
  
  // Determine if nodes overlap horizontally or vertically
  const horizontalOverlap = fromRight > toLeft && fromLeft < toRight;
  const verticalOverlap = fromBottom > toTop && fromTop < toBottom;
  
  // Calculate gaps between nodes
  const gapBelow = toTop - fromBottom;  // Positive if target is below
  const gapAbove = fromTop - toBottom;  // Positive if target is above
  const gapRight = toLeft - fromRight;  // Positive if target is to the right
  const gapLeft = fromLeft - toRight;   // Positive if target is to the left
  
  // Decision logic: Choose the connection that results in the shortest, cleanest path
  
  // Case 1: Target is clearly below (most common case in flowcharts)
  if (gapBelow > CLEARANCE && horizontalOverlap) {
    return { fromSide: ConnectionSide.BOTTOM, toSide: ConnectionSide.TOP, pathType: 'downward' };
  }
  
  // Case 2: Target is clearly above (back-edge / feedback loop)
  if (gapAbove > CLEARANCE && horizontalOverlap) {
    return { fromSide: ConnectionSide.TOP, toSide: ConnectionSide.BOTTOM, pathType: 'upward' };
  }
  
  // Case 3: Target is clearly to the right
  if (gapRight > CLEARANCE && verticalOverlap) {
    return { fromSide: ConnectionSide.RIGHT, toSide: ConnectionSide.LEFT, pathType: 'rightward' };
  }
  
  // Case 4: Target is clearly to the left
  if (gapLeft > CLEARANCE && verticalOverlap) {
    return { fromSide: ConnectionSide.LEFT, toSide: ConnectionSide.RIGHT, pathType: 'leftward' };
  }
  
  // Case 5: Diagonal positioning - choose based on which direction is more dominant
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);
  
  // If vertical distance is greater, prefer vertical connection
  if (absDy > absDx) {
    if (dy > 0) {
      return { fromSide: ConnectionSide.BOTTOM, toSide: ConnectionSide.TOP, pathType: 'downward' };
    } else {
      return { fromSide: ConnectionSide.TOP, toSide: ConnectionSide.BOTTOM, pathType: 'upward' };
    }
  } else {
    // Horizontal distance is greater
    if (dx > 0) {
      // Target is to the right-ish - check if vertical connection would be cleaner
      if (gapBelow > 0) {
        return { fromSide: ConnectionSide.BOTTOM, toSide: ConnectionSide.TOP, pathType: 'downward' };
      }
      return { fromSide: ConnectionSide.RIGHT, toSide: ConnectionSide.LEFT, pathType: 'rightward' };
    } else {
      // Target is to the left-ish
      if (gapBelow > 0) {
        return { fromSide: ConnectionSide.BOTTOM, toSide: ConnectionSide.TOP, pathType: 'downward' };
      }
      return { fromSide: ConnectionSide.LEFT, toSide: ConnectionSide.RIGHT, pathType: 'leftward' };
    }
  }
}

